/**
 * /agreement — the full Commercial License Agreement text.
 *
 * Verbatim from the monolith's `view === "agreement"` block. The sections live in
 * src/data/faq.ts (AGREEMENT_SECTIONS) — this is legal copy, so do not reword it.
 *
 * THE ONE PAGE IN THE APP THAT PRINTS ITSELF. Its Print button calls window.print() on
 * this page rather than building a document, because the agreement IS the legal text and
 * a second printable copy of legal copy is two versions that can drift. That makes the
 * screen the print master, which is why the branded masthead is a real element here
 * (PrintHeader, invisible until @media print) and why the page's own interface — the
 * Back link and the Print button — is marked `lyx-print-hide`. The print rules live at
 * the end of styles/global.css, including what was wrong before them: with no print
 * block at all this page printed light app text onto white paper.
 */
import { FileText, Printer } from 'lucide-react';
import { BackLink, PrintHeader } from '../components/ui';
import { AGREEMENT_SECTIONS } from '../data/faq';
import { AGREEMENT_UPDATED_LABEL } from '../data/license';
import { c } from '../styles/theme';
import { paths } from './paths';

export default function Agreement() {
  return (
    <div>
      {/* Nothing on screen; the masthead on paper. */}
      <PrintHeader kind="Commercial License Agreement" note={AGREEMENT_UPDATED_LABEL} />

      {/* A wrapper because BackLink takes no className and it is shared with four other
          pages that have no print behaviour to think about. A div, not a span: the link
          is a flex button, and an inline wrapper around a block-level box splits the
          inline flow rather than leaving the layout alone. */}
      <div className="lyx-print-hide">
        <BackLink fallbackTo={paths.licenses} fallbackLabel="Commercial License" />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 4,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileText size={17} color={c.accent} />
          <h1
            style={{
              fontFamily: 'Space Grotesk',
              fontWeight: 600,
              fontSize: 16,
              margin: 0,
              color: c.text1,
            }}
          >
            Commercial License Agreement
          </h1>
        </div>
        {/* Prints the page itself, not the certificate — the agreement is the
            legal document, the certificate is proof of purchase. Hidden on paper,
            where it is a control that cannot be pressed. */}
        <button
          type="button"
          onClick={() => window.print()}
          className="lyx-print-hide"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 12,
            color: c.text2,
            background: 'none',
            border: `1px solid ${c.border}`,
            borderRadius: 8,
            padding: '6px 10px',
            cursor: 'pointer',
          }}
        >
          <Printer size={12} />
          Print
        </button>
      </div>
      {/* The masthead already carries this line on paper, so it is hidden there
          rather than printed twice. */}
      <p className="lyx-print-hide" style={{ fontSize: 11, color: c.text3, margin: '0 0 18px' }}>
        {AGREEMENT_UPDATED_LABEL}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {AGREEMENT_SECTIONS.map((s) => (
          <section key={s.title}>
            <h2 style={{ fontWeight: 600, fontSize: 13, margin: '0 0 4px', fontFamily: 'Inter' }}>
              {s.title}
            </h2>
            <p style={{ fontSize: 12, color: c.text2, margin: 0, lineHeight: 1.7 }}>{s.body}</p>
          </section>
        ))}
      </div>
    </div>
  );
}
